import { GoogleGenAI, Type } from "@google/genai";
import { CreativeBrief, CampaignConcept } from "../types";

let runtimeApiKey = process.env.API_KEY || '';

export const setStoredApiKey = (key: string) => {
  runtimeApiKey = key;
};

const getAI = () => {
  if (!runtimeApiKey) {
     throw new Error("API_KEY_MISSING");
  }
  return new GoogleGenAI({ apiKey: runtimeApiKey });
};

const synthesizePrompt = (brief: CreativeBrief): string => {
  const physicalParts = `Physicality: ${brief.physique} build, ${brief.coatTexture} coat, ${brief.tailVolume} tail, ${brief.earPosture} ears.`;

  const segments = [
    `Subject: A high-fidelity, photorealistic ${brief.species}.`,
    `Photographic Intent: Candid, unpolished, high-dynamic-range wildlife editorial. No airbrushing.`,
    `Vibe: ${brief.vibe}.`,
    `Location: ${brief.scene}.`,
    `Lighting: ${brief.lighting}.`,
    `Camera: ${brief.cameraType}.`,
    `Optics: ${brief.lens}.`,
    `Expression: ${brief.expression}.`,
    `Eye Color: ${brief.eyeColor}.`,
    physicalParts,
    `Camera Angle: ${brief.cameraAngle}.`,
    `Detail Focus: Individual coarse fur fibers, moisture on the nose, realistic asymmetric eye reflections, natural skin folds, biological randomness.`,
  ].filter(Boolean);

  return segments.join(" ");
};

export const refineBriefPrompt = async (currentPrompt: string): Promise<string> => {
  try {
    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Transform this animal portrait prompt into a gritty, analog, and candid wildlife editorial directive: "${currentPrompt}". FOCUS ON: RAW camera sensor quality, natural fur physics (coarse/messy), subsurface scattering, and subtle technical photography flaws. STRICTLY AVOID: "Perfect" AI smoothness, plastic textures, or digital airbrushing. Keep it under 50 words.`,
    });
    return response.text?.trim() || currentPrompt;
  } catch (error) {
    console.error("Refinement failed", error);
    return currentPrompt;
  }
};

export const generateCampaignConcepts = async (brief: CreativeBrief): Promise<CampaignConcept[]> => {
  const ai = getAI();
  const synthPrompt = synthesizePrompt(brief);

  const systemInstruction = `YOU ARE A WORLD-CLASS WILDLIFE PHOTOGRAPHER AND DOCUMENTARIAN. 
  Storyboard 1 campaign theme for a ${brief.species} subject.
  FOCUS ON: RAW, unedited photographic quality. Capture natural, candid animal behavior.
  STRICT RULE: The subject's markings and face MUST match the anchor image exactly, but rendered with biological realism (irregular fur, natural grime, organic posing).
  AVOID: All "Dreamy," "Cyber," or "Symmetry" AI tropes. No plastic fur.
  ENFORCE: Camera sensor noise, natural lens flare, and shallow depth of field.
  Output JSON ARRAY ONLY with 1 item.`;

  const parts: any[] = [{ text: `BASE DIRECTIVE: ${synthPrompt}` }];
  
  if (brief.anchorImage) {
    const mime = brief.anchorImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.anchorImage.split(',')[1] } });
  }

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: { parts },
      config: {
        systemInstruction,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              themeIndex: { type: Type.NUMBER },
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              prompt: { type: Type.STRING },
            },
            required: ["id", "themeIndex", "title", "description", "prompt"],
          },
        },
      },
    });

    return JSON.parse(response.text?.trim() || "[]");
  } catch (error: any) {
    if (error.message?.includes("Requested entity was not found") || error.message === "API_KEY_MISSING") {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  conceptPrompt: string
): Promise<string> => {
  const ai = getAI();
  const model = 'gemini-3-pro-image-preview';
  
  const parts: any[] = [];
  
  if (brief.anchorImage) {
    const mime = brief.anchorImage.split(';')[0].split(':')[1] || 'image/jpeg';
    parts.push({ inlineData: { mimeType: mime, data: brief.anchorImage.split(',')[1] } });
  }

  const physicalityRef = `${brief.physique} build, ${brief.coatTexture} texture, ${brief.tailVolume} tail`;

  const finalPrompt = `
    PET IDENTITY ANCHOR: Use the attached photo as the EXACT identity reference. 
    SCENE: ${conceptPrompt}. 
    
    PHOTOREALISM MANDATE:
    - ANTI-SMOOTHING: Fur must look coarse, layered, and naturally imperfect. No plastic sheen.
    - BIOLOGICAL REALISM: Render saliva, wetness on the snout, individual eyebrow hairs, and natural eye-goop if appropriate.
    - LIGHTING: High-fidelity natural light with realistic shadows and highlights. No "perfect" studio glow.
    - OPTICS: Mimic a raw 85mm or 35mm lens. Include subtle lens grain and natural chromatic aberration.
    
    IDENTITY RULES: 
    - Unique spot patterns and ear geometry MUST match the reference exactly.
    - Preserve the specific eye color and pupil shape from the photo.
    
    TECHNICAL SPECS:
    - Camera: ${brief.cameraType}
    - Lighting: ${brief.lighting}
    - Optics: ${brief.lens}
    - Eye Detail: ${brief.eyeColor} with asymmetric catchlights.
    
    OUTPUT: A raw, candid, unedited wildlife masterpiece. 8k resolution.
  `.trim();

  parts.push({ text: finalPrompt });

  try {
    const response = await ai.models.generateContent({
      model,
      contents: { parts },
      config: {
        imageConfig: {
          aspectRatio: brief.aspectRatio,
          imageSize: brief.imageSize || "1K"
        },
        safetySettings: [
          { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_ONLY_HIGH' },
          { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_ONLY_HIGH' },
          { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_ONLY_HIGH' },
          { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_ONLY_HIGH' }
        ]
      },
    });

    const imagePart = response.candidates?.[0]?.content?.parts?.find(p => !!p.inlineData);
    if (imagePart?.inlineData) return `data:image/png;base64,${imagePart.inlineData.data}`;
    
    console.error("No image in response:", response);
    throw new Error("PRODUCTION_ERROR");
  } catch (error: any) {
    console.error("Gemini API Error:", error);
    if (error.message?.includes("Requested entity was not found") || error.message === "API_KEY_MISSING") {
      throw new Error("KEY_RESET_REQUIRED");
    }
    throw error;
  }
};